import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import { hexToRgbA } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dynamicClassName } from 'styles/helper'
import { colors } from 'styles/variables'
import NewIcon, { IconColor } from 'ui/atoms/NewIcon/NewIcon'
import styles from './NewButton.module.less'

type BaseButtonProps = {
  icon?: string
  iconSize?: 'small' | 'medium' | 'large'
  disabled?: boolean
  loading?: boolean
  tooltip?: {
    title: string
    placement?: 'top' | 'bottom' | 'left' | 'right'
  }
  isCollapsed?: boolean
  iconButton?: boolean
  children?: React.ReactNode
  maxWidth?: string
  style?: React.CSSProperties
  'data-testid'?: string
  'aria-expanded'?: boolean
  onClick: (event: React.MouseEvent<HTMLElement>) => void
}

type RequiredLabelVariants = 'primary' | 'outlined' | 'outlinedDanger' | 'optimize' | 'collapsible' | 'success'

type VariantWithRequiredLabel = BaseButtonProps & {
  variant: RequiredLabelVariants
  label: string
}

type VariantWithoutRequiredLabel = BaseButtonProps & {
  variant?: 'add' | 'save' | 'delete' | 'cancel' | 'edit'
  label?: string
}

type NewButtonProps = VariantWithRequiredLabel | VariantWithoutRequiredLabel

const variantIconColors: Record<string, IconColor> = {
  primary: 'white',
  success: 'white',
  add: 'white',
  save: 'white',
  delete: 'white',
  cancel: 'primary',
  edit: 'white',
  optimize: 'white',
  outlined: 'primary',
  outlinedDanger: 'danger',
  collapsible: 'primary',
}

const variantColors: Record<string, IconColor> = {
  primary: 'primary',
  outlined: 'primary',
  outlinedDanger: 'danger',
  success: 'success',
  add: 'primary',
  save: 'primary',
  delete: 'danger',
  cancel: 'primary',
  edit: 'primary',
  optimize: 'primary',
  collapsible: 'primary',
}

//Note to user:
// Try not to create a combo of NewButton with different icons and labels if not required. Try to use the already existing variants: primary, outlined, outlinedDanger, success, add, save, delete, cancel, edit, optimize, collapsible. The iconButton prop is a boolean flag (not a variant) that can be combined with these variants to render an icon-only button. See the Storybook for examples of how to use the NewButton component by using npm run storybook.

export default function NewButton({
  variant = 'primary',
  label,
  icon,
  iconSize = 'medium',
  disabled = false,
  loading = false,
  tooltip = {
    title: label ?? '',
    placement: 'top',
  },
  isCollapsed = false,
  iconButton = false,
  children,
  maxWidth,
  style,
  onClick,
  ...props
}: NewButtonProps): ReactElement {
  const { t } = useTranslation()

  const labelRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const checkOverflow = (): void => {
      if (labelRef.current) {
        setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth)
      }
    }

    checkOverflow()
  }, []) // Only check overflow on mount

  if (iconButton) {
    const iconColor = disabled ? 'grey' : (variantColors[variant] ?? 'primary')
    const variantIcons: Record<string, string> = {
      add: 'fal fa-plus',
      edit: 'fas fa-edit',
      delete: 'fas fa-trash',
      optimize: 'fal fa-redo-alt',
      cancel: 'fal fa-times',
    }
    const iconClassName = variant in variantIcons ? variantIcons[variant] : icon

    const colorToHex: Record<IconColor, string> = {
      primary: colors.primary5,
      black: colors.black,
      grey: colors.grey5,
      white: colors.white,
      info: colors.info4,
      warning: colors.warning4,
      danger: colors.danger4,
      success: colors.success4,
    }

    const hexColor = colorToHex[iconColor]

    const iconButtonSize: Record<string, string> = {
      small: '20px',
      medium: '30px',
      large: '40px',
    }

    return (
      <Tooltip arrow title={tooltip.title ?? label} placement={tooltip.placement}>
        <span>
          <IconButton
            aria-label={tooltip.title ?? label}
            aria-expanded={props['aria-expanded']}
            data-testid={props[`data-testid`]}
            disabled={disabled}
            style={style}
            sx={{
              width: iconButtonSize[iconSize],
              height: iconButtonSize[iconSize],
              color: hexColor,
              '&:hover': {
                backgroundColor: hexToRgbA(hexColor, 0.1),
              },
            }}
            onClick={onClick}
          >
            {loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : typeof iconClassName === `string` ? (
              <NewIcon ariaLabel={tooltip.title ?? label} icon={iconClassName} size={iconSize} color={iconColor} />
            ) : (
              iconClassName
            )}
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  const variantIcons = {
    add: 'fal fa-plus',
    optimize: 'fal fa-redo-alt',
  }

  const getCollapsibleIcon = () => {
    return isCollapsed ? 'fal fa-chevron-right' : 'fal fa-chevron-down'
  }

  const iconClassName =
    variant === 'collapsible'
      ? getCollapsibleIcon()
      : variant in variantIcons
        ? variantIcons[variant as keyof typeof variantIcons]
        : icon
  const iconColor = disabled ? 'grey' : (variantIconColors[variant] ?? 'primary')

  const variantLabels: Record<string, string> = {
    add: t('Add'),
    save: t('Save'),
    delete: t('Delete'),
    cancel: t('Cancel'),
    edit: t('Edit'),
  }
  const displayLabel = !label && variant in variantLabels ? variantLabels[variant] : label

  return (
    <Tooltip arrow title={tooltip.title === label && !isOverflowing ? '' : tooltip.title} placement={tooltip.placement}>
      <span className={styles.NewButton_ButtonWrapper}>
        <button
          onClick={onClick}
          disabled={disabled}
          style={maxWidth ? { maxWidth: maxWidth, ...style } : { ...style, cursor: disabled ? 'default' : 'pointer' }}
          data-testid={props[`data-testid`]}
          aria-label={label}
          aria-expanded={props['aria-expanded']}
          className={`${dynamicClassName({
            [styles.NewButton]: true,
            [styles.NewButton__disabled]: !!disabled,
            [styles.NewButton__primary]: variant === 'primary' && !disabled,
            [styles.NewButton__success]: variant === 'success' && !disabled,
            [styles.NewButton__outlined]: variant === 'outlined' && !disabled,
            [styles.NewButton__danger__outlined]: variant === 'outlinedDanger' && !disabled,
            [styles.NewButton__add]: variant === 'add' && !disabled,
            [styles.NewButton__save]: variant === 'save' && !disabled,
            [styles.NewButton__danger]: variant === 'delete' && !disabled,
            [styles.NewButton__cancel]: variant === 'cancel' && !disabled,
            [styles.NewButton__edit]: variant === 'edit' && !disabled,
            [styles.NewButton__optimize]: variant === 'optimize' && !disabled,
            [styles.NewButton__collapsible]: variant === 'collapsible' && !disabled,
          })}`}
        >
          <span className={styles.NewButton_Content}>
            {loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              iconClassName && (
                <span className={styles.NewButton__icon}>
                  <NewIcon ariaLabel={displayLabel ?? ''} icon={iconClassName} size={iconSize} color={iconColor} />
                </span>
              )
            )}
            {displayLabel && (
              <p className={maxWidth ? styles.NewButton_Label__maxWidth : styles.NewButton_Label} ref={labelRef}>
                {displayLabel}
              </p>
            )}
            {children}
          </span>
        </button>
      </span>
    </Tooltip>
  )
}
